/**
 * Runtime Context: Execution Context Management
 *
 * Manages runtime execution context, correlation IDs,
 * and execution metadata throughout the platform.
 *
 * @platform-infrastructure
 */

import { RuntimeContext, RuntimeExecutionId, CorrelationId } from '../contracts'

/**
 * Runtime Context Manager
 *
 * Manages execution context for the entire runtime lifecycle.
 * Provides correlation IDs, execution tracking, and metadata.
 */
export class RuntimeContextManager {
  private static currentContext: RuntimeContext | null = null
  private static executionStack: RuntimeContext[] = []

  /**
   * Generate a unique execution ID
   */
  static generateExecutionId(): RuntimeExecutionId {
    return `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Generate a correlation ID
   */
  static generateCorrelationId(): CorrelationId {
    return `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Create a new runtime context
   */
  static createContext(
    actor: string | null = null,
    route: string | null = null,
    service: string | null = null,
    domain: string | null = null,
    metadata: Record<string, any> = {}
  ): RuntimeContext {
    const context: RuntimeContext = {
      executionId: this.generateExecutionId(),
      correlationId: this.generateCorrelationId(),
      timestamp: new Date(),
      actor,
      route,
      service,
      domain,
      metadata,
    }

    // Add browser context if available
    if (typeof window !== 'undefined') {
      context.userAgent = navigator.userAgent
      context.metadata = {
        ...context.metadata,
        url: window.location.href,
        referrer: document.referrer,
      }
    }

    return context
  }

  /**
   * Set the current execution context
   */
  static setCurrentContext(context: RuntimeContext): void {
    this.currentContext = context
    this.executionStack.push(context)
  }

  /**
   * Get the current execution context
   */
  static getCurrentContext(): RuntimeContext | null {
    return this.currentContext
  }

  /**
   * Clear the current execution context
   */
  static clearCurrentContext(): void {
    if (this.executionStack.length > 0) {
      this.executionStack.pop()
      this.currentContext = this.executionStack[this.executionStack.length - 1] || null
    }
  }

  /**
   * Hard clear of all runtime execution contexts.
   *
   * Used during session/runtime teardown to prevent zombie execution chains.
   */
  static clearAll(): void {
    this.executionStack = []
    this.currentContext = null
  }

  /**
   * Execute a function within a runtime context
   */
  static async executeInContext<T>(
    context: RuntimeContext,
    fn: () => Promise<T>
  ): Promise<T> {
    const previousContext = this.currentContext
    this.setCurrentContext(context)

    try {
      return await fn()
    } finally {
      this.clearCurrentContext()
      if (previousContext) {
        this.setCurrentContext(previousContext)
      }
    }
  }

  /**
   * Get execution stack depth
   */
  static getExecutionDepth(): number {
    return this.executionStack.length
  }

  /**
   * Get all active execution contexts
   */
  static getExecutionStack(): RuntimeContext[] {
    return [...this.executionStack]
  }

  /**
   * Inherit correlation ID from parent context
   */
  static inheritCorrelationId(parentContext?: RuntimeContext): CorrelationId {
    return parentContext?.correlationId || this.generateCorrelationId()
  }

  /**
   * Create child context inheriting from parent
   */
  static createChildContext(
    parentContext: RuntimeContext,
    service?: string,
    domain?: string,
    metadata: Record<string, any> = {}
  ): RuntimeContext {
    return {
      ...parentContext,
      executionId: this.generateExecutionId(),
      timestamp: new Date(),
      service: service || parentContext.service,
      domain: domain || parentContext.domain,
      metadata: {
        ...parentContext.metadata,
        ...metadata,
        parentExecutionId: parentContext.executionId,
      },
    }
  }
}
